import { AgGridReact } from 'ag-grid-react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Col,
  Container,
  Form,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
} from 'reactstrap';
import { useHistory } from 'react-router-dom';

import { loadAdminManagerList, saveAdminUsers } from '../../../actions';

import Constants from '../../../services/Constants';
import LoadingComponentOverlay from '../../LoadingSpinner';
import SimpleTypeahead from '../../typeahead/SimpleTypeahead';

export const AdminManager = (props) => {
  const history = useHistory();

  const BY_USER = 'By User';
  const BY_PLAN = 'By Portfolio';

  const [selectedTab, setSelectedTab] = useState(BY_USER);
  const [selectedUser, setSelectedUser] = useState();
  const [selectedPlan, setSelectedPlan] = useState(Constants.ALL);
  const [managers, setManagers] = useState([]);

  useEffect(() => {
    if (props.okta.accessToken) {
      props.loadAdminManagerList();
    }
  }, [props.okta.accessToken]);

  useEffect(() => {
    setManagers(
      props.adminManagers.map((adm, idx) => ({
        ...adm,
        disabled: adm.email === props.okta.email,
        idx: idx,
      }))
    );
  }, [props.adminManagers]);

  const resetDataChanges = () => {
    const managerList = [];
    props.adminManagers.forEach((item, idx) =>
      managerList.push({ ...item, idx: idx })
    );
    setManagers([...managerList]);
  };

  const getPortfolios = () => {
    const portfolios = new Set();

    props.adminManagers.forEach((plan) => {
      if (selectedTab === BY_USER) {
        if (
          selectedUser != Constants.ALL &&
          plan.contactId === selectedUser?.contactId
        ) {
          portfolios.add(plan.planName);
        }
      } else {
        portfolios.add(plan.planName);
      }
    });

    const sortedPortfolios = [...portfolios].sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
    return selectedTab === BY_USER
      ? [Constants.ALL, ...sortedPortfolios]
      : [...sortedPortfolios];
  };

  const getUsers = () => {
    const newUsers = new Set();
    _.forEach(props.adminManagers, (user) => {
      if (selectedTab === BY_PLAN) {
        if (selectedPlan !== Constants.ALL && user.planName === selectedPlan) {
          newUsers.add(user.contactId);
        }
      } else {
        newUsers.add(user.contactId);
      }
    });

    const sortedUsers = [...newUsers]
      .map((usr) => props.adminManagers?.find((_usr) => _usr.contactId === usr))
      .sort((a, b) =>
        a.fullName.toLowerCase().localeCompare(b.fullName.toLowerCase())
      );
    return selectedTab === BY_PLAN
      ? [{ contactId: 0, fullName: Constants.ALL }, ...sortedUsers]
      : [...sortedUsers];
  };

  const availableUsers = useMemo(getUsers, [props.adminManagers, selectedPlan]);
  const availablePlans = useMemo(getPortfolios, [
    props.adminManagers,
    selectedUser,
  ]);

  const getGridData = () => {
    let filteredItems = [...managers];

    if (selectedTab == BY_PLAN && selectedPlan) {
      if (!filteredItems.find((i) => i.planName == selectedPlan)) {
        return [];
      }
      if (selectedUser.fullName !== Constants.ALL) {
        const userName = selectedUser.fullName
          ? selectedUser.fullName.toLowerCase()
          : selectedUser.toLowerCase();
        filteredItems = _.filter(filteredItems, (item) =>
          item.fullName?.toLowerCase().includes(userName)
        );
      }
      return filteredItems
        .filter((item) => item.planName.includes(selectedPlan))
        .sort((a, b) =>
          a.fullName.toLowerCase().localeCompare(b.fullName?.toLowerCase())
        );
    } else if (selectedTab == BY_USER && selectedUser) {
      if (selectedPlan !== Constants.ALL) {
        filteredItems = _.filter(filteredItems, (item) =>
          item.planName?.toLowerCase().includes(selectedPlan?.toLowerCase())
        );
      }
      return filteredItems
        .filter((item) => item.contactId === selectedUser.contactId)
        .sort((a, b) =>
          a.planName.toLowerCase().localeCompare(b.planName.toLowerCase())
        );
    }
    return [];
  };

  const getModifiedLines = () => {
    const modifiedUsers = managers.filter(
      (modUsr) =>
        modUsr.accessEnabled !=
        props.adminManagers.find((modMng, idx) => {
          return idx === modUsr.idx;
        }).accessEnabled
    );

    return modifiedUsers;
  };

  const AdminColumnDefs = [
    {
      field: 'accessEnabled',
      headerName: 'Enabled?',
      sortable: true,
      filter: true,
      resizable: true,
      editable: true,
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      width: 100,
      suppressHeaderFilterButton: true,
      suppressHeaderMenuButton: true,
    },
    {
      field: 'planName',
      headerName: 'Plan name',
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      hide: selectedTab === BY_PLAN,
      suppressHeaderFilterButton: true,
      suppressHeaderMenuButton: true,
    },
    {
      field: 'fullName',
      headerName: 'User name',
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      hide: selectedTab === BY_USER,
      suppressHeaderFilterButton: true,
      suppressHeaderMenuButton: true,
    },
  ];

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    if (tab === BY_USER) {
      setSelectedUser(null);
      setSelectedPlan(Constants.ALL);
    }
    if (tab === BY_PLAN) {
      setSelectedUser({ contactId: 0, fullName: Constants.ALL });
      setSelectedPlan(null);
    }
  };

  const handleUserSelect = (e) => {
    setSelectedUser(e);
    resetDataChanges();
    if (selectedTab === BY_USER) setSelectedPlan(Constants.ALL);
  };

  const handlePlanSelect = (e) => {
    setSelectedPlan(e);
    resetDataChanges();
    if (selectedTab === BY_PLAN)
      setSelectedUser({ contactId: 0, fullName: Constants.ALL });
  };

  const tabs = [BY_USER, BY_PLAN];
  const links = _.map(tabs, (l) => (
    <NavItem key={l} className="adminTab">
      <NavLink onClick={() => handleTabChange(l)} active={selectedTab === l}>
        {l}
      </NavLink>
    </NavItem>
  ));

  // eslint-disable-next-line react/prop-types
  const CustomUserOption = ({ innerProps, data }) => (
    // eslint-disable-next-line react/prop-types
    <div className="doctype-option" {...innerProps} key={data.contactId}>
      {/* eslint-disable-next-line react/prop-types */}
      {data.fullName}
    </div>
  );

  // eslint-disable-next-line react/prop-types
  const CustomPortfolioOption = ({ innerProps, data }) => (
    <div className="doctype-option" {...innerProps} key={data}>
      {data}
    </div>
  );

  const loadUsersOptions = (query, mustSelect = true) => {
    return new Promise((resolve) => {
      if (!query || query.length == 0) {
        resolve(availableUsers);
      }
      const options = _.chain(availableUsers)
        .filter((manager) => {
          return (manager.fullName || '')
            .toLowerCase()
            .includes(query.toLowerCase());
        })
        .map((manager) => {
          return {
            ...manager,
          };
        })
        .value();
      if (options.length === 0 && mustSelect) {
        resolve(availableUsers);
      }
      resolve(options);
    });
  };

  const loadPortfoliosOptions = (query, mustSelect = true) => {
    return new Promise((resolve) => {
      if (!query || query.length == 0) {
        resolve(availablePlans);
      }
      const options = _.chain(getPortfolios())
        .filter((manager) => {
          return (manager || '').toLowerCase().includes(query.toLowerCase());
        })
        .map((manager) => {
          return manager;
        })
        .value();
      if (options.length === 0 && mustSelect) {
        resolve(availablePlans);
      }
      resolve(options);
    });
  };

  const isUserSelectionValid = () => {
    return (
      selectedUser &&
      availableUsers?.find((u) => u.contactId == selectedUser.contactId)
    );
  };

  const isPortfolioSelectionValid = () => {
    return availablePlans?.find((p) => p == selectedPlan);
  };

  return (
    <Container>
      <Form className="pt-2 admin-manager">
        <Col className="justify-content-center">
          <Nav tabs className="mb-4">
            {links}
          </Nav>
          {Boolean(selectedTab === BY_USER) && (
            <Row className="justify-content-start">
              <Col md={6}>
                <Label>Select User</Label>
                <SimpleTypeahead
                  className="user-search"
                  id="select-user"
                  options={availableUsers}
                  loadOptions={loadUsersOptions}
                  onChange={(e) => handleUserSelect(e)}
                  value={selectedUser?.fullName || ''}
                  placeHolder="Select User"
                  customComponents={{
                    Option: CustomUserOption,
                  }}
                  isValid={isUserSelectionValid()}
                  alwaysShowOptions
                />
              </Col>
              <Col md={6}>
                <Label>Select Portfolio</Label>
                <SimpleTypeahead
                  id="select-portfolio"
                  options={availablePlans}
                  loadOptions={(e) => loadPortfoliosOptions(e, false)}
                  onChange={(e) => handlePlanSelect(e)}
                  value={selectedPlan}
                  placeHolder="Select Portfolio"
                  customComponents={{
                    Option: CustomPortfolioOption,
                  }}
                  isValid
                  alwaysShowOptions
                />
              </Col>
            </Row>
          )}
          {Boolean(selectedTab === BY_PLAN) && (
            <Row className="justify-content-start">
              <Col md={6}>
                <Label>Select Portfolio</Label>
                <SimpleTypeahead
                  className="user-search "
                  id="select-portfolio"
                  options={availablePlans}
                  loadOptions={loadPortfoliosOptions}
                  onChange={(e) => handlePlanSelect(e)}
                  value={selectedPlan}
                  placeHolder="Select Portfolio"
                  customComponents={{
                    Option: CustomPortfolioOption,
                  }}
                  isValid={isPortfolioSelectionValid()}
                  alwaysShowOptions
                />
              </Col>
              <Col md={6}>
                <Label>Select User</Label>
                <SimpleTypeahead
                  className="user-search"
                  id="select-user"
                  options={availableUsers}
                  loadOptions={(e) => loadUsersOptions(e, false)}
                  onChange={(e) => handleUserSelect(e)}
                  value={selectedUser?.fullName}
                  placeHolder="Select User"
                  customComponents={{
                    Option: CustomUserOption,
                  }}
                  isValid
                  alwaysShowOptions
                />
              </Col>
            </Row>
          )}
          <Row
            className="mt-3 mb-3 ag-theme-alpine"
            style={{ height: '20rem' }}>
            <Col lg={12}>
              <AgGridReact
                columnDefs={AdminColumnDefs}
                rowData={getGridData()}
                suppressScrollOnNewData={true}
                suppressDragLeaveHidesColumns={true}
                components={{ agLoadingOverlay: LoadingComponentOverlay }}
                icons={Constants.GRID_ICONS}
                onCellEditingStopped={({ data }) => {
                  const users = _.map(managers, (rowUser) => {
                    if (data.idx === rowUser.idx) {
                      return {
                        ...data,
                      };
                    }
                    return { ...rowUser };
                  });
                  return setManagers(users);
                }}
              />
            </Col>
          </Row>
          <div className="footer-buttons">
            <Button onClick={() => history.push(`/`)}>Cancel</Button>
            <Button
              color="primary"
              onClick={() => props.saveAdminUsers(getModifiedLines())}>
              Submit
            </Button>
          </div>
        </Col>
      </Form>
    </Container>
  );
};

AdminManager.propTypes = {
  loadAdminManagerList: PropTypes.func,
  saveAdminUsers: PropTypes.func,

  adminManagers: PropTypes.array,
  okta: PropTypes.object,
};

const mapStateToProps = (state) => {
  return {
    adminManagers: state.resources.admin_users?.data || [],
    okta: state.okta,
  };
};

export default connect(mapStateToProps, {
  saveAdminUsers,
  loadAdminManagerList,
})(AdminManager);
